import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { PDFForm } from 'pdf-lib';

import { Logger } from '@sondermind/datadog-logging';

import { W9TinType } from '../models/w9-tin-type';
import { IW9Interview } from './w9-interview.httpservice';

// Inputs the interactive render needs from the component's DOM
export interface W9RenderTargets {
  canvas: HTMLCanvasElement;
  annotationLayer: HTMLDivElement;
  container: HTMLDivElement;
}

// Called when the live TIN inputs change; component sets its signal from this
export type TinChangeHandler = (tin: string) => void;

const FIELD_LINE1 = 'topmostSubform[0].Page1[0].f1_01[0]'; // Legal name (Line 1)
const FIELD_LINE2 = 'topmostSubform[0].Page1[0].f1_02[0]'; // Business name (Line 2)
const FIELD_LLC_ELECTION = 'topmostSubform[0].Page1[0].Boxes3a-b_ReadOrder[0].f1_03[0]'; // LLC election letter
const FIELD_ADDRESS = 'topmostSubform[0].Page1[0].Address_ReadOrder[0].f1_07[0]'; // Address line
const FIELD_CITY_STATE_ZIP = 'topmostSubform[0].Page1[0].Address_ReadOrder[0].f1_08[0]'; // City/state/zip
const FIELD_REQUESTER_NAME = 'topmostSubform[0].Page1[0].f1_09[0]'; // Requester name
const FIELD_REQUESTER_ADDRESS = 'topmostSubform[0].Page1[0].f1_10[0]'; // Requester address
const FIELD_SSN_1 = 'topmostSubform[0].Page1[0].f1_11[0]';
const FIELD_SSN_2 = 'topmostSubform[0].Page1[0].f1_12[0]';
const FIELD_SSN_3 = 'topmostSubform[0].Page1[0].f1_13[0]';
const FIELD_EIN_1 = 'topmostSubform[0].Page1[0].f1_14[0]';
const FIELD_EIN_2 = 'topmostSubform[0].Page1[0].f1_15[0]';

const TIN_TIPS: Record<W9TinType, string> =
  {
    ssn: 'Enter your <strong>9-digit SSN</strong> exactly as it appears on your Social Security card. A single wrong digit triggers an IRS mismatch.',
    ein: 'Enter your <strong>9-digit EIN</strong>. Verify it against your IRS CP 575 assignment letter to avoid a mismatch.',
  };

export const SIG_TIP =
  'Type your <strong>full legal name</strong> to certify your TIN is correct. An unsigned W-9 is invalid per IRS rules.';

const HINT_PANEL_VISIBLE_CLASS = 'w9-review-page__hint-panel--visible';

// Max digit count per TIN segment — drives auto-advance on fill
const TIN_MAX_LENGTHS: Record<string, number> = {
  [FIELD_SSN_1]: 3,
  [FIELD_SSN_2]: 2,
  [FIELD_SSN_3]: 4,
  [FIELD_EIN_1]: 2,
  [FIELD_EIN_2]: 7,
};

// Line 3a checkbox mapping — keyed off the backend-derived `w9Line3a` value so the
// PDF renders exactly what agent-service (w9_mapping.py) decided. This is the single
// source of truth for IRS classification; the FE does NOT re-derive it from raw
// taxClassification/llcElection (that duplicated logic drifted out of sync).
// Non-LLC classifications map to c1_1[n]:
//   0=Individual/sole prop, 1=C corp, 2=S corp, 3=Partnership, 4=Trust/estate, 5=Other
const LINE3A_CHECKBOX_INDICES: Record<string, number> = {
  individual_sole_prop: 0,
  c_corporation: 1,
  s_corporation: 2,
  partnership: 3,
};
const CHECKBOX_BASE = 'topmostSubform[0].Page1[0].Boxes3a-b_ReadOrder[0].c1_1';

// LLC checkbox — c1_1[5] (the 6th Line 3a box, with the election-letter field
// f1_03 immediately after it) — checked for any LLC-classified w9Line3a value.
// NOTE: c1_2[0] is NOT the LLC box; it is the separate Line 3b (foreign-partner)
// checkbox. Pointing the LLC box at c1_2[0] left Line 3a blank and mis-checked 3b.
const FIELD_LLC_CHECKBOX = 'topmostSubform[0].Page1[0].Boxes3a-b_ReadOrder[0].c1_1[5]';

// LLC w9Line3a values → the election letter written next to the LLC box.
// `llc` (disregarded, no election) is intentionally left blank per the W-9 table
// decision — LLC box checked, no S/C/P letter.
const LINE3A_LLC_ELECTION_LETTERS: Record<string, string> = {
  llc: '',
  llc_s: 'S',
  llc_c: 'C',
};

// Static requester info pre-filled on all W-9s
const SONDERMIND_REQUESTER_NAME = 'SonderMind Inc.\n1099 18th St, Suite 2350\nDenver, CO 80202';

const DEFAULT_PDF_WIDTH = 800;
const ANNOTATION_MODE_ENABLE_FORMS = 2; // pdfjs annotationMode: ENABLE_FORMS

@Injectable({ providedIn: 'root' })
export class W9PdfService {
  private readonly platformId = inject(PLATFORM_ID);
  private renderListenerAbort?: AbortController;

  constructor(private logger: Logger) {}

  async fillAndFlatten(pdfBytes: Uint8Array, interview: IW9Interview, resolvedTinType?: W9TinType): Promise<Uint8Array> {
    const { PDFDocument } = await import('pdf-lib');
    const doc = await PDFDocument.load(pdfBytes);
    const form = doc.getForm();

    // Pre-filled provider fields
    this.trySetTextField(form, FIELD_LINE1, interview.w9Line1);
    this.trySetTextField(form, FIELD_LINE2, interview.w9Line2);
    // Line 5 is the street address only; the city/state/zip go on Line 6. Split
    // on the first comma so the street portion doesn't duplicate onto Line 5.
    // (mailingAddress format: "123 Main St, Denver CO 80203")
    this.trySetTextField(form, FIELD_ADDRESS, this.extractStreet(interview.mailingAddress));
    this.trySetLine3aCheckbox(form, interview.w9Line3a);

    // City/state/zip — derive from mailingAddress if it contains a comma
    const cityStateZip = this.extractCityStateZip(interview.mailingAddress);
    if (cityStateZip) {
      this.trySetTextField(form, FIELD_CITY_STATE_ZIP, cityStateZip);
    }

    // Static requester field — name + address on separate lines in single field
    this.trySetTextField(form, FIELD_REQUESTER_NAME, SONDERMIND_REQUESTER_NAME);

    // TIN fields stay EMPTY and LIVE — user types their full TIN
    const tinType: W9TinType = resolvedTinType ?? interview.tinType ?? 'ssn';
    const liveTinFields = new Set<string>(
      tinType === 'ssn'
        ? [FIELD_SSN_1, FIELD_SSN_2, FIELD_SSN_3]
        : [FIELD_EIN_1, FIELD_EIN_2]
    );

    // Mark all non-TIN fields read-only
    form.getFields().forEach((field) => {
      if (!liveTinFields.has(field.getName())) {
        try {
          field.enableReadOnly();
        } catch {
          // Some field types may not support enableReadOnly — ignore silently
        }
      }
    });

    return doc.save();
  }

  private trySetTextField(form: PDFForm, fieldName: string, value: string | null | undefined): void {
    if (!value) return;
    try {
      form.getTextField(fieldName).setText(value);
    } catch {
      this.logger.warn(`W9PdfService: field not found — ${fieldName}`);
    }
  }

  // Render the Line 3a checkbox from the backend-derived w9Line3a value. Any
  // LLC-classified value checks the LLC box (with its election letter, blank for
  // plain disregarded `llc`); all others check the matching c1_1[n] box.
  private trySetLine3aCheckbox(form: PDFForm, w9Line3a: string | null | undefined): void {
    if (!w9Line3a) return;

    if (w9Line3a in LINE3A_LLC_ELECTION_LETTERS) {
      try {
        form.getCheckBox(FIELD_LLC_CHECKBOX).check();
      } catch {
        this.logger.warn(`W9PdfService: LLC checkbox not found — ${FIELD_LLC_CHECKBOX}`);
      }
      const letter = LINE3A_LLC_ELECTION_LETTERS[w9Line3a];
      if (letter) {
        this.trySetTextField(form, FIELD_LLC_ELECTION, letter);
      }
      return;
    }

    const idx = LINE3A_CHECKBOX_INDICES[w9Line3a];
    if (idx === undefined) {
      this.logger.warn(`W9PdfService: unknown w9Line3a value — ${w9Line3a}`);
      return;
    }
    const fieldName = `${CHECKBOX_BASE}[${idx}]`;
    try {
      form.getCheckBox(fieldName).check();
    } catch {
      this.logger.warn(`W9PdfService: checkbox not found — ${fieldName}`);
    }
  }

  private extractStreet(mailingAddress: string | null | undefined): string | null {
    if (!mailingAddress) return null;
    const commaIdx = mailingAddress.indexOf(',');
    // No comma → treat the whole value as the street so Line 5 still populates.
    if (commaIdx === -1) return mailingAddress.trim();
    return mailingAddress.slice(0, commaIdx).trim();
  }

  private extractCityStateZip(mailingAddress: string | null | undefined): string | null {
    if (!mailingAddress) return null;
    const commaIdx = mailingAddress.indexOf(',');
    if (commaIdx === -1) return null;
    return mailingAddress.slice(commaIdx + 1).trim();
  }

  async fetchPdfBytes(assetUrl = 'assets/w9.pdf'): Promise<Uint8Array> {
    const response = await fetch(assetUrl);
    if (!response.ok) throw new Error(`Failed to fetch w9.pdf: ${response.status}`);
    const buffer = await response.arrayBuffer();
    return new Uint8Array(buffer);
  }

  // Mirrors w9_mapping.py — derive tinType from taxClassification + llcElection
  // when the agent hasn't set it yet (tinType: null on the interview; SE cannot
  // persist tinType, so this fallback is required rather than reading it back).
  // Only sole_prop uses the SSN; every EIN path — including a disregarded LLC
  // (llcElection 'none') per the W-9 table decision — resolves to 'ein'.
  deriveTinType(interview: IW9Interview): W9TinType {
    const { taxClassification } = interview;
    if (!taxClassification) return 'ssn';
    if (taxClassification === 'sole_prop') return 'ssn';
    return 'ein';
  }

  private getTinFieldNames(tinType: W9TinType): Set<string> {
    if (tinType === 'ssn') {
      return new Set([FIELD_SSN_1, FIELD_SSN_2, FIELD_SSN_3]);
    }
    return new Set([FIELD_EIN_1, FIELD_EIN_2]);
  }

  private ensureViewerCss(): void {
    // Lazy-load pdf_viewer.css so annotation-layer inputs are positioned correctly.
    if (document.getElementById('pdfjs-viewer-css')) return;
    const link = document.createElement('link');
    link.id = 'pdfjs-viewer-css';
    link.rel = 'stylesheet';
    link.href = 'assets/pdf_viewer.css';
    document.head.appendChild(link);
  }

  private showHint(panel: HTMLElement, containerEl: HTMLElement, inputEl: HTMLElement, tipHtml: string): void {
    const panelEl = panel;
    panelEl.innerHTML = tipHtml;
    const containerRect = containerEl.getBoundingClientRect();
    const inputRect = inputEl.getBoundingClientRect();
    const panelLeft = containerRect.right + 14;
    const panelTop = Math.min(Math.max(inputRect.top - 6, 10), window.innerHeight - 160);
    panelEl.style.left = `${panelLeft}px`;
    panelEl.style.top = `${panelTop}px`;
    panelEl.classList.add(HINT_PANEL_VISIBLE_CLASS);
  }

  private hideHint(panel: HTMLElement): void {
    panel.classList.remove(HINT_PANEL_VISIBLE_CLASS);
  }

  async renderInteractivePdf(
    targets: W9RenderTargets,
    pdfBytes: Uint8Array,
    tinType: W9TinType,
    onTinChange: TinChangeHandler,
    hintPanel?: HTMLElement
  ): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    // Reset any prior render so a refetch (agent field update) does not stack a
    // second annotation layer with duplicate TIN inputs over the first.
    const { annotationLayer: layerToReset, canvas: canvasToReset } = targets;
    layerToReset.replaceChildren();
    layerToReset.removeAttribute('style');
    const priorCtx = canvasToReset.getContext('2d');
    if (priorCtx) priorCtx.clearRect(0, 0, canvasToReset.width, canvasToReset.height);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pdfjsLib: any = await import('pdfjs-dist');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, import/extensions
    const { SimpleLinkService } = await import('pdfjs-dist/web/pdf_viewer.mjs') as any;

    this.ensureViewerCss();

    pdfjsLib.GlobalWorkerOptions.workerSrc = 'assets/pdf.worker.min.mjs';

    const loadingTask = pdfjsLib.getDocument({ data: pdfBytes });
    const pdfDoc = await loadingTask.promise;
    const page = await pdfDoc.getPage(1);

    const { canvas, annotationLayer: annotationLayerDiv, container } = targets;
    const context = canvas.getContext('2d');
    if (!context) return;

    const containerWidth = container.offsetWidth || DEFAULT_PDF_WIDTH;
    const naturalViewport = page.getViewport({ scale: 1 });
    const scale = containerWidth / naturalViewport.width;
    const viewport = page.getViewport({ scale });

    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;

    // eslint-disable-next-line prefer-destructuring
    const annotationStorage = pdfDoc.annotationStorage;

    await page.render({
      canvasContext: context,
      viewport,
      annotationMode: ANNOTATION_MODE_ENABLE_FORMS,
      annotationStorage,
    }).promise;

    annotationLayerDiv.style.width = `${viewport.width}px`;
    annotationLayerDiv.style.height = `${viewport.height}px`;
    annotationLayerDiv.style.setProperty('--scale-factor', String(scale));

    const annotations = await page.getAnnotations();
    const linkService = new SimpleLinkService();

    const annotationLayer = new pdfjsLib.AnnotationLayer({
      div: annotationLayerDiv,
      page,
      viewport: viewport.clone({ dontFlip: true }),
      accessibilityManager: null,
      annotationCanvasMap: null,
      annotationEditorUIManager: null,
    });

    await annotationLayer.render({
      viewport: viewport.clone({ dontFlip: true }),
      div: annotationLayerDiv,
      annotations,
      page,
      linkService,
      annotationStorage,
      renderForms: true,
      enableScripting: false,
      hasJSActions: false,
      fieldObjects: null,
    });

    const tinFieldNames = this.getTinFieldNames(tinType);

    this.wireTinFieldListeners(annotationLayerDiv, tinFieldNames, tinType, onTinChange, hintPanel, container);
  }

  private wireTinFieldListeners(
    annotationLayerDiv: HTMLDivElement,
    tinFieldNames: Set<string>,
    tinType: W9TinType,
    onTinChange: TinChangeHandler,
    hintPanel: HTMLElement | undefined,
    container: HTMLDivElement
  ): void {
    // Tear down listeners from a prior render before binding fresh ones.
    this.renderListenerAbort?.abort();
    this.renderListenerAbort = new AbortController();

    const updateTinValue = (): void => {
      const parts: string[] = [];
      tinFieldNames.forEach((fieldName) => {
        const el = annotationLayerDiv.querySelector<HTMLInputElement>(`[name="${fieldName}"]`);
        if (el) parts.push(el.value);
      });
      onTinChange(parts.join(''));
    };

    const updateHintCounter = (): void => {
      const panel = hintPanel;
      if (!panel?.classList.contains(HINT_PANEL_VISIBLE_CLASS)) return;
      const digits = Array.from(tinFieldNames)
        .map((n) => annotationLayerDiv.querySelector<HTMLInputElement>(`[name="${n}"]`)?.value ?? '')
        .join('')
        .replace(/\D/g, '');
      const baseTip = panel.dataset['baseTip'] ?? panel.innerHTML;
      panel.dataset['baseTip'] = baseTip;
      let counterHtml = '';
      if (digits.length > 0 && digits.length < 9) {
        counterHtml = `<div style="margin-top:0.5rem;font-size:0.75rem;font-weight:600;color:#E8A87C;">${digits.length} / 9 digits</div>`;
      } else if (digits.length >= 9) {
        const label = tinType === 'ssn' ? 'SSN' : 'EIN';
        counterHtml = `<div style="margin-top:0.5rem;font-size:0.75rem;font-weight:600;color:#6FD9B0;">✓ ${label} complete</div>`;
      }
      panel.innerHTML = baseTip + counterHtml;
    };

    tinFieldNames.forEach((fieldName) => {
      const input = annotationLayerDiv.querySelector(`[name="${fieldName}"]`);
      if (input) {
        input.removeAttribute('hidden');
        input.removeAttribute('disabled');
        if (hintPanel) {
          const panel = hintPanel;
          input.addEventListener('focus', () => {
            this.showHint(panel, container, input as HTMLElement, TIN_TIPS[tinType]);
            panel.dataset['baseTip'] = panel.innerHTML;
            updateHintCounter();
          });
          input.addEventListener('blur', () => this.hideHint(panel));
        }
      }
    });

    const tinFieldOrder = Array.from(tinFieldNames);

    const getInput = (name: string): HTMLInputElement | null => annotationLayerDiv.querySelector<HTMLInputElement>(`[name="${name}"]`);

    // Div-level listeners are torn down via renderListenerAbort on each re-render
    // (see above); the layer div persists across refetches, so we must not stack them.
    annotationLayerDiv.addEventListener('input', (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (!target) return;
      const fieldName = target.getAttribute('data-field-name') ?? target.name ?? '';
      if (!tinFieldNames.has(fieldName)) return;

      updateTinValue();
      updateHintCounter();

      // Auto-advance to the next segment when this one is full.
      const maxLen = TIN_MAX_LENGTHS[fieldName];
      if (maxLen !== undefined && target.value.length >= maxLen) {
        const idx = tinFieldOrder.indexOf(fieldName);
        const nextField = idx >= 0 ? tinFieldOrder[idx + 1] : undefined;
        if (nextField) getInput(nextField)?.focus();
      }
    }, { signal: this.renderListenerAbort.signal });

    annotationLayerDiv.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key !== 'Backspace') return;
      const target = event.target as HTMLInputElement;
      if (!target) return;
      const fieldName = target.getAttribute('data-field-name') ?? target.name ?? '';
      if (!tinFieldNames.has(fieldName)) return;

      // On backspace in an empty segment, jump back to the previous one.
      if (target.value.length === 0) {
        const idx = tinFieldOrder.indexOf(fieldName);
        const prevField = idx > 0 ? tinFieldOrder[idx - 1] : undefined;
        if (prevField) {
          const prevInput = getInput(prevField);
          if (prevInput) {
            prevInput.focus();
            // Place cursor at end of previous field so the next backspace deletes its last char.
            const len = prevInput.value.length;
            prevInput.setSelectionRange(len, len);
          }
        }
      }
    }, { signal: this.renderListenerAbort.signal });
  }
}
